import { createContext, createElement, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from '@g17eco/atoms/loader';
import { useAppSelector } from '@reducers/index';
import { checkIsBottomOfInitiativeTree } from '@selectors/initiative';
import { getCompletedFiscalYear } from '../components/FiscalYearSelector';
import { useGetActivityYearsQuery } from '../api/emissions';

const STORAGE_KEY = 'g17-emissions-filter-prefs';

interface EmissionsFilterPrefs {
  fiscalYear: number;
  includeChildren: boolean;
}

const readPrefs = (): Partial<EmissionsFilterPrefs> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Partial<EmissionsFilterPrefs>) : {};
  } catch {
    return {};
  }
};

const writePrefs = (prefs: Partial<EmissionsFilterPrefs>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readPrefs(), ...prefs }));
  } catch {
    // localStorage may be unavailable (private browsing, storage quota, etc.)
  }
};

interface EmissionsFiltersValue {
  hasChildren: boolean;
  selectedFiscalYear: number;
  setSelectedFiscalYear: (year: number) => void;
  availableYears: number[] | undefined;
  includeChildren: boolean;
  setIncludeChildren: (value: boolean) => void;
}

const EmissionsFiltersContext = createContext<EmissionsFiltersValue | null>(null);

interface ProviderProps {
  initiativeId: string;
  children: ReactNode;
}

// Validates stored filter prefs against current initiative's realities (hasChildren, availableYears)
// before rendering children. This guarantees every downstream query mounts with correct, stable
// params — no duplicate requests while state catches up.
const EmissionsFiltersGate = ({ initiativeId, children }: ProviderProps) => {
  const isLeafInitiative = useAppSelector(checkIsBottomOfInitiativeTree);
  const hasChildren = !isLeafInitiative;

  const { data: availableYears, isSuccess: yearsLoaded } = useGetActivityYearsQuery(initiativeId);

  const [selectedFiscalYear, setSelectedFiscalYearState] = useState<number | undefined>(undefined);
  const [includeChildren, setIncludeChildrenState] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!yearsLoaded) return;
    const stored = readPrefs();
    const completed = getCompletedFiscalYear();
    const isValidYear = (y: number | undefined): y is number =>
      typeof y === 'number' && (availableYears?.includes(y) ?? false);

    const year = isValidYear(stored.fiscalYear)
      ? stored.fiscalYear
      : isValidYear(completed)
        ? completed
        : (availableYears && availableYears.length > 0 ? Math.max(...availableYears) : completed);

    if (stored.fiscalYear !== year) writePrefs({ fiscalYear: year });
    setSelectedFiscalYearState(year);

    const storedChildren = stored.includeChildren ?? true;
    const validChildren = hasChildren ? storedChildren : false;
    if (stored.includeChildren !== validChildren) writePrefs({ includeChildren: validChildren });
    setIncludeChildrenState(validChildren);
  }, [yearsLoaded, availableYears, hasChildren]);

  const setSelectedFiscalYear = useCallback((year: number) => {
    setSelectedFiscalYearState(year);
    writePrefs({ fiscalYear: year });
  }, []);

  const setIncludeChildren = useCallback((value: boolean) => {
    setIncludeChildrenState(value);
    writePrefs({ includeChildren: value });
  }, []);

  if (selectedFiscalYear === undefined || includeChildren === undefined) {
    return createElement(Loader);
  }

  const value: EmissionsFiltersValue = {
    hasChildren,
    selectedFiscalYear,
    setSelectedFiscalYear,
    availableYears,
    includeChildren,
    setIncludeChildren,
  };

  return createElement(EmissionsFiltersContext.Provider, { value }, children);
};

// Wrapper that reads initiativeId from the route and mounts the gate. Use this to wrap any
// route whose components consume useEmissionsFilters() — children won't mount until filter
// state is validated against the current initiative.
export const WithEmissionsFilters = ({ children }: { children: ReactNode }) => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  if (!initiativeId) return createElement(Loader);
  return createElement(EmissionsFiltersGate, { initiativeId, children });
};

export const useEmissionsFilters = (): EmissionsFiltersValue => {
  const ctx = useContext(EmissionsFiltersContext);
  if (!ctx) {
    throw new Error('useEmissionsFilters must be used within <WithEmissionsFilters>');
  }
  return ctx;
};
